import { ACCOUNT_ORDER_STATUS, orderSourceType, PAY_METHOD, SOURCE_TYPE } from "./enums.js";
import Store from "../store/store.js";
import { fixedNumber } from "./math-util.js";
import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import i18n from "../i18n.js";

export const distributeAmount = (total, payments, maxBonus, minCardAmount = 5) => {
    if (maxBonus === 0) {
        payments = payments.filter(p => p.paymethod_id !== PAY_METHOD.bonus);
    }
    const bonusPayment = payments.find(p => !p.isTouched && p.paymethod_id === PAY_METHOD.bonus);
    const touchedPayments = payments.filter(p => p.isTouched);
    const untouchedPayments = payments.filter(p => !p.isTouched && p.paymethod_id !== PAY_METHOD.bonus);
    const totalInCent = total * 100;

    const touchedSum = touchedPayments.reduce((acc, cv) => acc += cv.saleAmount * 100, 0);
    let restSum = totalInCent - touchedSum;

    if (touchedSum < total * 100 && maxBonus && bonusPayment) {
        const bonusInCent = maxBonus * 100;
        const sumForBonus = touchedSum + bonusInCent <= restSum
            ? bonusInCent
            : totalInCent - touchedSum >= bonusInCent
                ? bonusInCent
                : totalInCent - touchedSum;
        restSum -= sumForBonus;
        bonusPayment.saleAmount = sumForBonus;
        bonusPayment.changed = true;
    }

    if (touchedSum === totalInCent && maxBonus && bonusPayment) {
        bonusPayment.saleAmount = 0;
        restSum = totalInCent - touchedPayments.reduce((acc, cv) => acc += cv.saleAmount * 100, 0);
    }

    const basePayment = untouchedPayments.length ? Math.floor(restSum / untouchedPayments.length) : 0;
    const remainingAmount = untouchedPayments.length ? restSum % untouchedPayments.length : 0;
    const minCardAmountInCent = minCardAmount * 100;

    for (let i = 0; i < untouchedPayments.length; i++) {
        const isCard = [PAY_METHOD.creditCard, PAY_METHOD.terminal].includes(untouchedPayments[i].paymethod_id);
        untouchedPayments[i] = {
            ...untouchedPayments[i],
            saleAmount: isCard
                ? basePayment === 0 ? minCardAmountInCent : (basePayment < minCardAmountInCent ? minCardAmountInCent : basePayment)
                : (basePayment < 0 ? 0 : basePayment)
        };
    }

    if (remainingAmount) {
        untouchedPayments[untouchedPayments.length - 1].saleAmount += remainingAmount;
    }

    const totalUntouched = untouchedPayments.reduce((acc, cv) => acc += cv.saleAmount, 0);
    const totalTouched = touchedPayments.reduce((acc, cv) => acc += (cv.saleAmount || 0) * 100, 0);
    const lastTotal = totalTouched + totalUntouched + (bonusPayment && bonusPayment.changed ? bonusPayment.saleAmount : 0);
    if (totalInCent < lastTotal) {
        let valueToRemove = totalInCent - lastTotal;
        untouchedPayments.forEach(item => {
            if (valueToRemove) {
                const isCard = [PAY_METHOD.creditCard, PAY_METHOD.terminal].includes(item.paymethod_id);
                const newAmount = item.saleAmount + valueToRemove;
                if (isCard) {
                    const value = newAmount >= minCardAmountInCent ? newAmount : minCardAmountInCent;
                    valueToRemove = valueToRemove + item.saleAmount - value;
                    item.saleAmount = value;
                } else {
                    const value = newAmount >= 0 ? newAmount : 0;
                    valueToRemove = valueToRemove + item.saleAmount - value;
                    item.saleAmount = value;
                }
            }
        });

        if (bonusPayment) {
            if (bonusPayment.changed) {
                const newAmount = bonusPayment.saleAmount + valueToRemove;
                const value = newAmount >= 0 ? newAmount : 0;
                valueToRemove = valueToRemove + bonusPayment.saleAmount - value;
                bonusPayment.saleAmount = newAmount;
            } else {
                const newAmount = bonusPayment.saleAmount * 100 + valueToRemove;
                const value = newAmount >= 0 ? newAmount : 0;
                valueToRemove = valueToRemove + bonusPayment.saleAmount * 100 - value;
                bonusPayment.saleAmount = newAmount;
            }
        } else {
            // change bonus value even if it was touched;
            const bonusChanged = payments.find(p => p.paymethod_id === PAY_METHOD.bonus);
            if (bonusChanged && ((bonusChanged.saleAmount * 100 + totalInCent - lastTotal) > 0)) {
                bonusChanged.saleAmount = (bonusChanged.saleAmount * 100 + totalInCent - lastTotal) / 100;
            }
        }
    }

    if (bonusPayment) {
        if (bonusPayment.changed) {
            bonusPayment.saleAmount = bonusPayment.saleAmount / 100;
            bonusPayment.changed = undefined;
            // delete bonusPayment.changed;
        }
        return [bonusPayment, ...touchedPayments, ...untouchedPayments.map(p => ({ ...p, saleAmount: p.saleAmount / 100 }))];
    } else {
        return [...touchedPayments, ...untouchedPayments.map(p => ({ ...p, saleAmount: p.saleAmount / 100 }))];
    }
}

export const filtratePayments = (payments, newPayment) => {
    const executeType = {
        [PAY_METHOD.creditCard]: [PAY_METHOD.terminal],
        [PAY_METHOD.cash]: [PAY_METHOD.terminal],
        [PAY_METHOD.bonus]: [PAY_METHOD.voucher],
        [PAY_METHOD.discount]: [],
        [PAY_METHOD.terminal]: [PAY_METHOD.cash, PAY_METHOD.creditCard],
        [PAY_METHOD.voucher]: [PAY_METHOD.bonus, PAY_METHOD.voucher],
    }[newPayment.paymethod_id] || [];
    return payments.filter(p => !executeType.includes(p.paymethod_id)).concat(newPayment);
}

export const getOrderSource = (source, sourceId) => {
    if (!sourceId) {
        return orderSourceType()[source];
    }
    if (source === SOURCE_TYPE.delivapp) {
        const providers = Store?.store?.getState()?.restaurant?.data?.ordersProvider || [];
        const provider = providers.find(p => p.id === sourceId);
        return provider ? (provider.name || provider.id) : orderSourceType()[source];
    }

    return orderSourceType()[source];
}

export const getTotalPaymentForOrder = (payments) => {
    return (payments || []).reduce((acc, payment) => {
        return {
            cash: fixedNumber(acc.cash + (payment.IsCache ? payment.Amount : 0), 2) || 0,
            card: fixedNumber(acc.card + (!payment.IsCache ? payment.Amount : 0), 2) || 0,
        }
    }, {
        cash: 0,
        card: 0,
    })
}
export const getCardMasksForOrder = (payments) => {
    return [...new Set((payments || []).reduce((acc, cv) => {
        if(cv.paymethod_id === PAY_METHOD.creditCard) return acc.concat(cv.card_mask);
        return acc;
    }, []))].join(', ');
}

export const isDateOverdue = (start, end) => {
    const startTime = moment(start);
    const endTime = moment(end);

    if (!startTime.isValid() || !endTime.isValid()) {
        return false;
    }

    return startTime.isAfter(endTime);
};

const formatDeliveryDateTime = (datetime) => {
    const deliveryMoment = moment(datetime);
    const today = moment().startOf('day');
    const deliveryDay = deliveryMoment.clone().startOf('day');

    if (deliveryDay.isAfter(today)) {
        return deliveryMoment.format('DD/MM HH:mm');
    }

    return deliveryMoment.format('HH:mm');
};

export const useGetDeliveryInfo = ({
    pickupEta,
    dropOffEta,
    status,
    deliveryDatetime,
    actualDeliveryDatetime,
    t
}) => {
    const [deliveryInfo, setDeliveryInfo] = useState(null);

    const updateDeliveryInfo = useCallback(() => {
        if (pickupEta || dropOffEta) {
            setDeliveryInfo(null);

            return;
        }

        const warningDescription = dropOffEta
            ? t('draft.deliveryOverdueExtended', {
                estimatedTimeArrival: moment(dropOffEta).format('HH:mm'),
                committedTimeArrival: moment(deliveryDatetime).format('HH:mm')
            })
            : t('draft.deliveryOverdue', {
                date: moment(deliveryDatetime).format('HH:mm')
            });

        switch (status) {
            case ACCOUNT_ORDER_STATUS.pending:
            case ACCOUNT_ORDER_STATUS.accepted:
            case ACCOUNT_ORDER_STATUS.ready:
            case ACCOUNT_ORDER_STATUS.delivering:
                setDeliveryInfo(
                    deliveryDatetime
                        ? {
                            label: t('draft.estimate'),
                            value: formatDeliveryDateTime(deliveryDatetime),
                            isWarning: isDateOverdue(new Date(), deliveryDatetime),
                            warningDescription
                        }
                        : null
                );
                break;

            case ACCOUNT_ORDER_STATUS.finished:
                if (actualDeliveryDatetime) {
                    setDeliveryInfo({
                        label: t('draft.delivery'),
                        value: moment(actualDeliveryDatetime).format('HH:mm'),
                        isWarning: isDateOverdue(actualDeliveryDatetime, deliveryDatetime),
                        warningDescription: t('draft.deliveryOverdue', {
                            date: moment(actualDeliveryDatetime).format('HH:mm')
                        })
                    });
                } else {
                    setDeliveryInfo(
                        deliveryDatetime
                            ? {
                                label: t('draft.delivery'),
                                value: moment(deliveryDatetime).format('HH:mm'),
                                isWarning: isDateOverdue(new Date(), deliveryDatetime),
                                warningDescription: t('draft.deliveryOverdue', {
                                    date: moment(deliveryDatetime).format('HH:mm')
                                })
                            }
                            : null
                    );
                }
                break;

            default:
                setDeliveryInfo(null);
        }
    }, [pickupEta, dropOffEta, status, deliveryDatetime, actualDeliveryDatetime, t]);

    useEffect(() => {
        updateDeliveryInfo();
        const intervalId = setInterval(updateDeliveryInfo, 60000);

        return () => clearInterval(intervalId);
    }, [updateDeliveryInfo]);

    return deliveryInfo;
};

export const getHumanizeTimeDuration = (startDateStr, endDateStr = null) => {
    const startDate = new Date(startDateStr);
    const endDate = endDateStr ? new Date(endDateStr) : new Date();

    const seconds = Math.abs(endDate.getTime() - startDate.getTime()) / 1000;

    const days = Math.floor(seconds / 60 / 60 / 24);
    const hours = Math.floor(seconds / 60 / 60) - 24 * days;
    const minutes = Math.floor(seconds / 60) - days * 24 * 60 - hours * 60;

    const daysStr = days > 0 ? `${days} ${i18n.t('draft.days')} ` : '';
    const hoursStr = hours > 0 ? `${hours} ${i18n.t('draft.hours')} ` : '';
    const minutesStr = minutes >= 0 ? `${minutes} ${i18n.t('draft.minutes')} ` : '';

    const isMoreThenOneDay = days >= 1;
    return isMoreThenOneDay ? `${daysStr}`.trim() : `${daysStr}${hoursStr}${minutesStr}`.trim();
};

export const getOrderDate = (creationDatetime) => {
    const seconds = Math.abs(new Date().getTime() - new Date(creationDatetime).getTime()) / 1000;
    const is12HoursHavePassed = seconds / 60 > 12;
    const isCurrentDate = new Date().toDateString() === new Date(creationDatetime).toDateString();
    const isShowDate = !isCurrentDate && is12HoursHavePassed;
    const format24Hours = isShowDate ? 'DD/MM | HH:mm' : 'HH:mm';

    if (seconds < 60) {
        return moment(creationDatetime).format(format24Hours);
    }
    const timeAgo = getHumanizeTimeDuration(creationDatetime);

    return `${moment(creationDatetime).format(format24Hours)} (${i18n.t('draft.ago', { time: timeAgo })})`;
};

export const formatDateTimePreOrder = (date) => {
    const now = moment();
    const deliveryDate = moment(date);

    if (deliveryDate.isSame(now, 'day')) {
        return deliveryDate.format('HH:mm');
    }

    return deliveryDate.format(`${'DD/MM | HH:mm'}`);
};

export const getPaymentIntentModel = (body, payment) => {
    return {
        business_id: body.business_id,
        products: body.products && body.products.map(pr => ({
            ...pr,
            full_price: pr.full_price
        })),
        delivery_type: body.delivery_type,
        delivery_datetime: body.delivery_datetime,
        location: body.location,
        coupon:	body.coupon,
        requested_bonus: body.requested_bonus,
        driver_tip: body.driver_tip,
        email: body.email,
        sale_amount: payment.sale_amount,
        paymethod_id: PAY_METHOD.creditCard,
        payment_data: payment.payment_data,
        card_expiry: payment.card_expiry,
    }
}

export const promise3DSTranzilla = (challenge) => {
    return new Promise((resolve, reject) => {
        const ds = window.open(challenge, '3ds', 'width=500,height=700');

        if (!ds) {
            reject({status: 'blocked'});
            return;
        }

        const listener = (message) => {
            if (message.origin === "https://3ds.delivapp.com") {
                cleanup();
                resolve(message.data);
            }
        };

        const interval = setInterval(() => {
            if (ds.closed) {
                cleanup();
                reject({status: 'closed'});
            }
        }, 500);

        const cleanup = () => {
            clearInterval(interval);
            window.removeEventListener('message', listener);
            if(ds) {
                ds.close();
            }
        };

        window.addEventListener('message', listener);
    });
};